// React import
import { useState } from 'react';

// Mui imports
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';

// Local imports
import currencyToText from '../../CurrencyToText';
import CurrencyDialog from '../../CurrencyDialog';

function CurrencyTextField(props) {
    const { value, setValue, focused, setFocused, focusedId, currency, setCurrency, onBlur, onCancelBlur = () => {}, disabled, width, inputRef, forceTextField, organization, hasNextButton, placeholder } = props;

    const [ currencyDialogOpen, setCurrencyDialogOpen ] = useState(false);

    const _value = (() => {
        if (focused === focusedId) return value || '';
        if (isNaN(parseFloat(value))) return value || '';
        return currencyToText(currency, true, value.toLocaleString());
    })();

    if (focused !== focusedId && !forceTextField) {
        return (
            <Box sx={{ padding: '18px 12px 18px 12px', width: '182px' }}>
                 {_value}
            </Box>
        )
    }

    const showButton = forceTextField || focused === focusedId;
    const inputWidth = width || (showButton ? '200px' : '100px');
    
    return (
        <Box sx={{ position: 'relative', width: inputWidth, display: 'flex', alignItems: 'stretch', transition: 'width 0.2s ease' }}>
            <TextField
                sx={{
                    '& .MuiOutlinedInput-root': {
                        backgroundColor: '#ffffff',
                        borderRadius: showButton ? '6px 0px 0px 6px' : '6px',
                        borderTopRightRadius: showButton ? 0 : '6px',
                        borderBottomRightRadius: showButton ? 0 : '6px',
                        boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)',
                        '&.Mui-focused': {
                            '& fieldset': {
                                borderRight: showButton ? 'none !important' : '1px solid #3b82f6',
                                borderColor: '#3b82f6',
                            },
                        },
                        '& fieldset': {
                            borderColor: '#E5E7EB',
                            borderRight: showButton ? 'none' : '1px solid #E5E7EB',
                        },
                        '&.Mui-disabled': {
                            backgroundColor: 'rgba(0, 0, 0, 0.06)',
                            '& fieldset': {
                                borderColor: 'rgba(0, 0, 0, 0.23)',
                                borderRight: showButton ? 'none' : '1px solid rgba(0, 0, 0, 0.23)',
                            },
                        },
                    },
                }}
                inputRef={inputRef}
                value={_value}
                onChange={e => setValue(e.target.value)}
                fullWidth
                placeholder={placeholder}
                onFocus={() => {
                    if (setFocused) {
                        setFocused(focusedId)
                    }
                }}
                onBlur={() => {
                    if (onBlur) {
                        onBlur(focusedId);
                    }
                }} 
                onKeyDown={e => {
                    if (e.keyCode === 13) {
                        if (onBlur) {
                            onBlur(focusedId);
                        }
                        e.preventDefault();
                        e.stopPropagation();
                    }
                }}
                disabled={disabled}
            />
            {showButton && (
                <Button
                    disabled={disabled}
                    variant='text'
                    color='primary'
                    size='small'
                    className={focused === focusedId ? 'currency-button-focused' : 'currency-button-unfocused'}
                    sx={{ 
                        minWidth: 'auto',
                        padding: '0px 12px',
                        fontSize: '13px',
                        fontWeight: 500,
                        textTransform: 'none',
                        borderLeft: 'none',
                        borderRadius: hasNextButton ? '0px' : '0px 6px 6px 0px',
                        alignSelf: 'stretch',
                        boxShadow: 'none',
                        marginTop: 0,
                        marginBottom: 0,
                        height: 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        // Default (unfocused, not disabled)
                        color: '#667085',
                        border: '1px solid #E5E7EB',
                        backgroundColor: '#ffffff',
                        '&:hover:not(.Mui-disabled)': {
                            backgroundColor: '#dbeafe',
                            boxShadow: 'none',
                            borderColor: '#E5E7EB',
                        },
                        // Focused state
                        '&.currency-button-focused': {
                            borderColor: '#3b82f6 !important',
                            color: '#3b82f6',
                            backgroundColor: '#eff6ff',
                            '&:hover:not(.Mui-disabled)': {
                                borderColor: '#3b82f6 !important',
                                backgroundColor: '#dbeafe',
                            },
                        },
                        // Unfocused state
                        '&.currency-button-unfocused': {
                            borderColor: '#E5E7EB !important',
                            color: '#667085',
                            backgroundColor: '#ffffff',
                            '&:hover:not(.Mui-disabled)': {
                                borderColor: '#E5E7EB !important',
                                backgroundColor: '#dbeafe',
                            },
                        },
                        // Disabled state - matches MUI TextField disabled border
                        '&.Mui-disabled': {
                            backgroundColor: 'rgba(0, 0, 0, 0.06)',
                            borderColor: 'rgba(0, 0, 0, 0.23) !important',
                            color: 'rgba(0, 0, 0, 0.38)',
                            cursor: 'not-allowed',
                        },
                    }}
                    onClick={() => {
                        if (!disabled) {
                            onCancelBlur();
                            setCurrencyDialogOpen(true);
                        }
                    }}
                    onMouseDown={(e) => {
                        if (!disabled) {
                            e.preventDefault(); // Prevent blur of input
                        }
                    }}
                >
                    {currencyToText(currency, true)}
                </Button>
            )}
            <CurrencyDialog
                open={currencyDialogOpen} 
                onClose={() => setCurrencyDialogOpen(false)}
                initialCurrency={currency} 
                onCurrency={currency => {
                    setCurrency(currency)
                    setCurrencyDialogOpen(false);
                }}
                defaultCurrency={organization.defaults?.currency}
                extraCurrencies={organization.defaults?.extraCurrencies}
            />
        </Box>
    );
}

export default CurrencyTextField;
